import { apiGet, apiPost } from '../api';
import { useMutation, useQuery, keepPreviousData } from '@tanstack/react-query';
import { QueryCacheKeys } from '@/api/queryKeys';
import { createUrl } from '@/util/url';
import { MINUTE } from '@/constant/time';
import { MentalHealthReport } from './report.types';

/** 서버에 리포트가 아직 없으면 404 가 아니라 PENDING 이 온다 — 폴링이 의도된 흐름이다. */
export type MindHealthReportStatus =
  | 'PENDING'
  | 'SUCCEEDED'
  | 'INSUFFICIENT_DATA'
  | 'FAILED';

/** 목록·대시보드가 blob 을 파싱하지 않고 쓰는 요약. 전 필드가 null 일 수 있다. */
export interface MindHealthReportSummary {
  mindScore: number | null;
  decisionLabel: string | null;
  stressScore: number | null;
  stressCategory: string | null;
  balanceFlag: string | null;
  respirationState: string | null;
  analyzability: number | null;
  coverage: number | null;
  durationMin: number | null;
}

export interface MindHealthReportDetail {
  sessionId: string;
  status: MindHealthReportStatus;
  algorithmVersion: string | null;
  /** ISO 문자열 (밀리초 포함) */
  computedAt: string | null;
  summary: MindHealthReportSummary | null;
  report: MentalHealthReport | null;
  failureReason: string | null;
}

export interface MindHealthReportListItem {
  sessionId: string;
  status: Exclude<MindHealthReportStatus, 'PENDING'>;
  computedAt: string | null;
  /** 측정 기준 시작 시각. 세션의 startTime 과는 다른 값이다 */
  measuredAt: string | null;
  algorithmVersion: string | null;
  summary: MindHealthReportSummary;
}

export interface Pagination {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalEntries: number;
}

export interface MindHealthReportListResponse {
  data: MindHealthReportListItem[];
  pagination: Pagination;
}

export type MindHealthAnalysisState = 'ANALYZED' | 'INSUFFICIENT' | 'UNMEASURED';

export interface MindHealthDirectoryRow {
  protecteeId: number;
  name: string;
  gender: number | null;
  birthDate: string | null;
  analysisState: MindHealthAnalysisState;
  /** 상태와 무관한 전체 리포트 건수 */
  reportCount: number;
  latestReport:
    | (Omit<MindHealthReportListItem, 'status'> & {
        status: Exclude<MindHealthReportStatus, 'PENDING'>;
      })
    | null;
}

export interface MindHealthDirectoryCounts {
  total: number;
  analyzed: number;
  insufficient: number;
  unmeasured: number;
}

export interface MindHealthDirectoryResponse {
  data: MindHealthDirectoryRow[];
  pagination: Pagination;
  /** 필터와 무관하게 검색어만 반영한다 — 탭을 옮겨도 상단 카드가 흔들리지 않는다 */
  counts: MindHealthDirectoryCounts;
}

export interface GetDirectoryParams {
  pageNum?: number;
  pageSize?: number;
  query?: string;
  filter?: 'all' | 'analyzed' | 'insufficient' | 'unmeasured';
  sortBy?: 'analyzed:desc' | 'name:asc' | 'name:desc';
}

export interface GetReportsParams {
  pageNum?: number;
  pageSize?: number;
  /**
   * `computedAt` 은 계산한 시각이라 백필로 한 번에 만든 리포트는 값이 거의 같다.
   * 화면이 보여주는 값은 측정 시각이므로 기본은 `measuredAt:desc` 를 쓴다.
   */
  sortBy?:
    | 'measuredAt:asc'
    | 'measuredAt:desc'
    | 'computedAt:asc'
    | 'computedAt:desc';
}

/**
 * AI 건강분석 회원 목록
 * GET /mind-health/protectees
 *
 * 회원 행·최신 리포트 요약·건수·상단 카운트를 한 번에 준다.
 */
export const apiGetMindHealthDirectory = async (params?: GetDirectoryParams) => {
  return await apiGet<MindHealthDirectoryResponse>(
    createUrl('/mind-health/protectees', params)
  );
};

export const useQueryGetMindHealthDirectory = (params?: GetDirectoryParams) => {
  return useQuery({
    queryKey: QueryCacheKeys.mentalHealth.getDirectory(params),
    queryFn: async () => {
      const { data } = await apiGetMindHealthDirectory(params);
      return data;
    },
    // 페이지·필터를 바꿀 때 목록이 빈 화면으로 깜빡이지 않게 한다.
    placeholderData: keepPreviousData,
  });
};

/**
 * 보호대상자의 리포트 이력
 * GET /protectee/{protecteeId}/mind-health/reports
 *
 * 요약이 인라인이라 행마다 따로 불러올 필요가 없다.
 */
export const apiGetMindHealthReports = async (
  protecteeId: number,
  params?: GetReportsParams
) => {
  return await apiGet<MindHealthReportListResponse>(
    createUrl(`/protectee/${protecteeId}/mind-health/reports`, params)
  );
};

export const useQueryGetMindHealthReports = (
  protecteeId: number,
  params?: GetReportsParams,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: QueryCacheKeys.mentalHealth.getReports(protecteeId, params),
    queryFn: async () => {
      const { data } = await apiGetMindHealthReports(protecteeId, params);
      return data;
    },
    enabled: enabled && protecteeId > 0,
    retry: false,
  });
};

/**
 * 리포트 단건
 * GET /protectee/{protecteeId}/mind-health/sessions/{sessionId}/report
 */
export const apiGetMindHealthReport = async (
  protecteeId: number,
  sessionId: string
) => {
  return await apiGet<MindHealthReportDetail>(
    `/protectee/${protecteeId}/mind-health/sessions/${sessionId}/report`
  );
};

/** 계산이 끝날 때까지 폴링할 최대 횟수. 2초 간격이므로 약 2분이다. */
export const REPORT_POLL_LIMIT = 60;

export const useQueryGetMindHealthReport = (
  protecteeId: number,
  sessionId: string | null | undefined,
  enabled: boolean = true,
  poll: boolean = false
) => {
  return useQuery({
    queryKey: QueryCacheKeys.mentalHealth.getReport(protecteeId, sessionId ?? ''),
    queryFn: async () => {
      const { data } = await apiGetMindHealthReport(protecteeId, sessionId!);
      return data;
    },
    enabled: enabled && protecteeId > 0 && !!sessionId,
    // 계산 전이면 서버가 PENDING 을 주면서 큐에 넣는다. 끝나면 폴링을 멈춘다.
    // 큐가 비었거나 워커가 느릴 수 있어 상한을 둔다 — 무한 폴링은 멈추지 않는 화면이 된다.
    refetchInterval: (query) =>
      poll &&
      query.state.data?.status === 'PENDING' &&
      query.state.dataUpdateCount < REPORT_POLL_LIMIT
        ? 2000
        : false,
    // 리포트는 sessionId 로 고정된 불변 기록이다. 회원을 오가도 다시 받지 않는다.
    staleTime: 5 * MINUTE,
    retry: false,
  });
};

/**
 * 리포트 재계산 요청
 * POST /protectee/{protecteeId}/mind-health/sessions/{sessionId}/report/recompute
 *
 * 202 는 "큐에 넣었다"는 뜻이지 "계산이 끝났다"는 뜻이 아니다. 결과는 단건 조회로 본다.
 */
export const apiRecomputeMindHealthReport = async (
  protecteeId: number,
  sessionId: string,
  force: boolean = false
) => {
  return await apiPost<{
    sessionId: string;
    status: 'PENDING';
    enqueued: boolean;
  }>(`/protectee/${protecteeId}/mind-health/sessions/${sessionId}/report/recompute`, {
    force,
  });
};

export const useRecomputeMindHealthReport = () => {
  return useMutation({
    mutationFn: ({
      protecteeId,
      sessionId,
      force,
    }: {
      protecteeId: number;
      sessionId: string;
      force?: boolean;
    }) => apiRecomputeMindHealthReport(protecteeId, sessionId, force),
  });
};
